import { useContext, useState, useRef, useEffect } from 'react';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import JarFlyerboardContext from '@/context/JarFlyerboardContext';
import { Tag } from '@/design-system';
import { css } from '@emotion/css';
import TuneIcon from '@mui/icons-material/Tune';
import colors from '@/theme/colors';
import { JAR_TAG_FILTERS, JarTagFilter } from '@/context/JarFlyerboardProvider';
import { OwningTypeEnum } from '@/graphql/types';
import { JarFilter } from './JarFilter';
import { AccessibleDivButton, ToggleViewControl } from '@/components';

export const JarFilterButtons: React.FC = () => {
  const { drawerFilters, toggleQuickFilter, amountActiveFilters, trackingFrom } =
    useContext(JarFlyerboardContext);

  const { scrollY } = useScroll();
  const [hidden, setHidden] = useState(false);
  const lastToggleYRef = useRef<number>(0);
  const distanceInDirectionRef = useRef<number>(0);
  const lastDirectionRef = useRef<'down' | 'up'>('down');
  const lastToggleAtRef = useRef<number>(0);

  // Asymmetric thresholds to reduce flicker
  const HIDE_DISTANCE_PX = 64; // need more distance to hide
  const SHOW_DISTANCE_PX = 36; // need less distance to show
  const MIN_TOGGLE_INTERVAL_MS = 500; // debounce layout/animation-induced jitter
  const EPSILON = 0.5; // ignore tiny changes

  useEffect(() => {
    const initial = scrollY.get();
    lastToggleYRef.current = initial;
    if (initial <= 0) {
      setHidden(false);
    }
  }, [scrollY]);

  useMotionValueEvent(scrollY, 'change', current => {
    const prev = scrollY.getPrevious() ?? current;
    const diff = current - prev;
    const now = Date.now();

    // Always show at top
    if (current <= 0) {
      if (hidden) {
        setHidden(false);
        lastToggleAtRef.current = now;
        lastToggleYRef.current = current;
      }
      distanceInDirectionRef.current = 0;
      lastDirectionRef.current = 'down';
      return;
    }

    if (Math.abs(diff) < EPSILON) return;

    const direction: 'down' | 'up' = diff > 0 ? 'down' : 'up';

    // Reset accumulation on direction change
    if (direction !== lastDirectionRef.current) {
      distanceInDirectionRef.current = 0;
      lastDirectionRef.current = direction;
    }

    // Accumulate absolute distance while moving in a direction
    distanceInDirectionRef.current += Math.abs(diff);

    // Debounce toggles
    if (now - lastToggleAtRef.current < MIN_TOGGLE_INTERVAL_MS) return;

    if (!hidden && direction === 'down') {
      if (distanceInDirectionRef.current >= HIDE_DISTANCE_PX) {
        setHidden(true);
        lastToggleAtRef.current = now;
        lastToggleYRef.current = current;
        distanceInDirectionRef.current = 0;
      }
    } else if (hidden && direction === 'up') {
      if (distanceInDirectionRef.current >= SHOW_DISTANCE_PX) {
        setHidden(false);
        lastToggleAtRef.current = now;
        lastToggleYRef.current = current;
        distanceInDirectionRef.current = 0;
      }
    }
  });

  const filterOptions: JarTagFilter[] = Object.values(JAR_TAG_FILTERS);

  const onToggle = (option: JarTagFilter) => {
    toggleQuickFilter(option);
  };

  const isQuickFilterActive = (tag: JarTagFilter): boolean => {
    if (tag === JAR_TAG_FILTERS.SCHOOL) {
      return drawerFilters.owningType === OwningTypeEnum.district;
    } else if (tag === JAR_TAG_FILTERS.COMMUNITY) {
      return drawerFilters.owningType === OwningTypeEnum.organization;
    } else if (tag === JAR_TAG_FILTERS.FREE) {
      return drawerFilters.isCommunityFree === true;
    }
    return false;
  };

  return (
    <AnimatePresence initial={false} mode="wait">
      {!hidden && (
        <motion.div
          className={cn.filtersWrapper}
          key="filters-wrapper"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ height: { duration: 0.3, ease: 'easeInOut' }, opacity: { duration: 0.2 } }}
          style={{ overflow: 'hidden' }}
        >
          <div className={cn.filters}>
            <JarFilter>
                <Tag
                  rightIcon={<TuneIcon sx={{ fontSize: '15px' }} />}
                  variant={amountActiveFilters ? 'active' : 'outline'}
                  className={cn.filtersTag}
                  nonTranslate
                >
                  {amountActiveFilters > 0 ? (
                    <span key="badge" className={cn.badge}>
                      {amountActiveFilters}
                    </span>
                  ) : null}
                </Tag>
            </JarFilter>
            {filterOptions.map(option => (
              <AccessibleDivButton
                onClick={() => onToggle(option)}
                key={option}
                role="button"
                aria-label={`Select ${option} filter`}
              >
                <Tag
                  key={option}
                  variant={isQuickFilterActive(option) ? 'active' : 'outline'}
                  className={cn.tagOption}
                >
                  {option}
                </Tag>
              </AccessibleDivButton>
            ))}
            <div className={cn.buttonGrid}>
              <ToggleViewControl trackingFrom={trackingFrom} />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const cn = {
  filtersWrapper: css`
    background: ${colors.white};
    width: 100%;
    z-index: 1198;
    overflow: hidden;
    padding: 2px;
  `,
  filters: css`
    display: flex;
    flex-wrap: nowrap;
    padding: 0px 16px 16px;
    gap: 8px;
  `,
  filtersTag: css`
    width: 52px;
    justify-content: center;
  `,
  tagOption: css`
    text-transform: capitalize;
  `,
  badge: css`
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${colors.jungle};
    color: ${colors.white};
    border-radius: 50%;
    height: 14px;
    width: 14px;
    text-align: center;
    font-size: 10px;
    line-height: 16px;
    letter-spacing: -0.3px;
  `,
  buttonGrid: css`
    margin-left: auto;
  `,
};
